import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
    CollectionResponse,
    defaultResponseMessageObj,
    ResponseMessageObj,
} from "../types/ApiResults";
import { PaginationEnum } from "../domain/enums/common/PaginationEnum";
import { DEFAULT_PAGE } from "../domain/constants";
import { ResponseMessage } from "../domain/enums/common/ResponseMessageEnum";
import { EntityService } from "../types/EntityService";
import { Langs } from "../domain/enums/common/LangsEnum";
import { translationServiceFactory } from "../services/TranslationServiceImpl";

function mergeTranslations(existing: any[] = [], updated: any[] = []) {
    const map = new Map<string, any>();
    existing.forEach((t) => map.set(t.languageCode, t));
    updated.forEach((t) =>
        map.set(t.languageCode, { ...map.get(t.languageCode), ...t })
    );
    return Array.from(map.values());
}

/**
 * Custom hook to manage entity state and operations.
 */
export function useEntityManager<
    T extends { id?: string; translations?: any[] },
    F
>({
    service,
    defaultLimit = PaginationEnum.SHOW_20,
    defaultFilters,
}: {
    service: EntityService<T, F>;
    defaultLimit?: number;
    defaultFilters?: F;
}) {
    const [collection, setCollection] = useState<CollectionResponse<T>>({
        rows: [],
        totalPages: 0,
        totalRows: 0,
    });
    const [page, setPage] = useState(DEFAULT_PAGE);
    const [limit, setLimit] = useState(defaultLimit);
    const [filters, setFilters] = useState<F>(defaultFilters ?? ({} as F));
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState<T | null>(null);
    const [isEditorVisible, setEditorVisible] = useState(false);
    const [messageObj, setMessageObj] = useState<ResponseMessageObj>(
        defaultResponseMessageObj
    );

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const res = await service.getAll(page, limit, filters);
            res && setCollection(res);
        } catch (e) {
            console.error(e);
            setMessageObj({
                id: uuidv4(),
                messageType: ResponseMessage.DANGER,
                messageKey: "errors.common",
            });
        } finally {
            setLoading(false);
        }
    }, [page, limit, filters, service]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const openEditor = useCallback(
        async (id?: string) => {
            try {
                setEditing((id && (await service.getById(id))) || null);
                setEditorVisible(true);
            } catch {
                setMessageObj({
                    id: uuidv4(),
                    messageType: ResponseMessage.DANGER,
                    messageKey: "errors.common",
                });
            }
        },
        [service]
    );

    const applyUpdate = useCallback((saved: T) => {
        setCollection((prev) => {
            const exists = prev.rows.some((r) => r?.id === saved?.id);
            if (exists) {
                return {
                    ...prev,
                    rows: prev.rows.map((r) =>
                        r?.id === saved?.id
                            ? service.instantiate({
                                  ...r,
                                  ...saved,
                                  translations: mergeTranslations(
                                      r.translations,
                                      saved.translations ?? []
                                  ),
                              })
                            : r
                    ),
                };
            }
            // new item -> insert at top
            return {
                ...prev,
                rows: [service.instantiate(saved), ...prev.rows],
            };
        });
    }, []);

    const handleDelete = useCallback(
        async (id: string) => {
            const { status } = await service.deleteById(id);
            setMessageObj({
                id: uuidv4(),
                messageType: !status
                    ? ResponseMessage.DANGER
                    : ResponseMessage.SUCCESS,
                messageKey: !status
                    ? "errors.deleteFailed"
                    : "console.common.deleteSuccess",
            });
            if (!status) return;

            // assume success if no throw
            setCollection((prev) => ({
                ...prev,
                rows: prev.rows.filter((r) => r?.id !== id),
            }));
        },
        [service]
    );

    const handleSave = useCallback(
        async (saved: T) => {
            let updated: T;
            try {
                updated = await service.createOrUpdate(saved);
                applyUpdate(updated);
            } catch (e) {
                console.error(e);
            } finally {
                setMessageObj({
                    id: uuidv4(),
                    messageType: !updated
                        ? ResponseMessage.DANGER
                        : ResponseMessage.SUCCESS,
                    messageKey: !updated
                        ? "errors.common"
                        : "console.common.success",
                });
            }
        },
        [service, applyUpdate]
    );

    const translateFields = useCallback(
        async (
            translationFields: Record<string, string>,
            sourceLang: Langs,
            targetLang: Langs
        ) => {
            let translated: Record<string, string>;
            try {
                translated =
                    await translationServiceFactory().translateObjectFields(
                        translationFields,
                        sourceLang,
                        targetLang
                    );
                return translated;
            } catch (e) {
                console.error(e);
            } finally {
                setMessageObj({
                    id: uuidv4(),
                    messageType: !translated
                        ? ResponseMessage.DANGER
                        : ResponseMessage.SUCCESS,
                    messageKey: !translated
                        ? "errors.common"
                        : "console.common.success",
                });
            }
        },
        []
    );

    return {
        collection,
        page,
        limit,
        filters,
        loading,
        editing,
        isEditorVisible,
        messageObj,
        setPage,
        setLimit,
        setFilters,
        setEditorVisible,
        setEditing,
        setMessageObj,
        fetchAll,
        openEditor,
        handleDelete,
        handleSave,
        translateFields,
    };
}
